import { useCallback } from "react";
import {
  BILLINGS_LINK,
  INCOMLEND_INTERNAL_LINK,
  LEADS,
  LEADS_TABLE,
  MAPS,
  WITHDRAWL_DEPOSIT_LINK,
} from "codes";
import { parseWithdrawalDepositCommentEntityId } from "helpers/withdrawalDepositComments";
import { useUpdateMarkAsReadMutation } from "api/api-notifications";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { accountrtk } from "api/api-accounts";
import history from "helpers/history";
import { useAppDispatch } from "redux/hooks";
import type { INotification } from "api/api-notifications.d";
import { UsersTableHighlightQueryKey } from "helpers/bookMeetingConstants";

const BOOK_MEETING_NOTIFICATION_CATEGORY = "book_meeting";

const isBookMeetingNotification = (notification: INotification) =>
  notification.category === BOOK_MEETING_NOTIFICATION_CATEGORY ||
  notification.category?.startsWith("/incomlend/opportunity/") === true;

export const useNotificationHandler = () => {
  const [markNotificationRead] = useUpdateMarkAsReadMutation();
  const { data: currentUser } = useGetCurrentUserQuery();
  const dispatch = useAppDispatch();

  const handleNotificationClick = useCallback(
    async (notification: INotification) => {
      try {
        // Map comment mention: open that map and its comment modal
        if (notification.category === "map_comment_mention") {
          const mapId =
            (notification as any).entity_id || notification.context?.entity_id;
          if (mapId) {
            sessionStorage.setItem("openMapCommentModal", "true");
            sessionStorage.setItem("mapCommentModalMapId", mapId);
            await markNotificationRead(notification.id).unwrap();
            history.push(`${INCOMLEND_INTERNAL_LINK}${MAPS}/${mapId}`);
            // If user was already on this map, component won't remount; event opens modal
            window.dispatchEvent(
              new CustomEvent("openMapCommentModal", { detail: { mapId } }),
            );
          }
          return;
        }

        // Setup fees paid (billing) notification: navigate to the linked MAP
        if (notification.category === "new_billing") {
          const mapId = notification.context?.map_id;
          const billingId =
            (notification as any).entity_id || notification.context?.entity_id;

          await markNotificationRead(notification.id).unwrap();

          if (mapId) {
            history.push(`${INCOMLEND_INTERNAL_LINK}${MAPS}/${mapId}`);
            return;
          }

          if (billingId) {
            history.push(
              `${INCOMLEND_INTERNAL_LINK}${BILLINGS_LINK}/${billingId}`,
            );
          }
          return;
        }

        // Invoice comment notification: open invoice verification / comments section
        if (notification.category === "invoice_comment") {
          const invoiceId =
            (notification as any).entity_id ||
            notification.context?.entity_id ||
            notification.context?.invoice_id;
          if (invoiceId) {
            sessionStorage.setItem("openInvoiceCommentSection", "true");
            sessionStorage.setItem(
              "invoiceCommentSectionInvoiceId",
              String(invoiceId),
            );
            await markNotificationRead(notification.id).unwrap();
            history.push(
              `${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/invoice/${invoiceId}`,
            );
            window.dispatchEvent(
              new CustomEvent("openInvoiceCommentSection", {
                detail: { invoiceId: String(invoiceId) },
              }),
            );
          }
          return;
        }

        // Finance assignment notification: open the related invoice.
        if (notification.category === "invoice_finance_assignment") {
          const invoiceId =
            notification.entity_id ||
            notification.context?.entity_id ||
            notification.context?.invoice_id;

          if (invoiceId) {
            await markNotificationRead(notification.id).unwrap();
            history.push(
              `${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/invoice/${invoiceId}`,
            );
          }
          return;
        }

        const wdCommentCategories = new Set([
          "withdrawal_deposit_comment",
          "withdrawal_deposit_mention",
        ]);
        const wdNavCategories = new Set([
          "withdrawal_deposit_created",
          "withdrawal_deposit_operation",
          "withdrawal_deposit_completed",
          ...wdCommentCategories,
        ]);
        const wdFormIdFromEntity = wdNavCategories.has(notification.category)
          ? parseWithdrawalDepositCommentEntityId(
              (notification as any).entity_id ||
                notification.context?.entity_id,
            )
          : null;
        if (wdNavCategories.has(notification.category) || wdFormIdFromEntity) {
          const formId = notification.context?.form_id || wdFormIdFromEntity;
          if (formId) {
            if (wdCommentCategories.has(notification.category)) {
              sessionStorage.setItem("openWdCommentModal", "true");
              sessionStorage.setItem("wdCommentModalFormId", String(formId));
            }
            await markNotificationRead(notification.id).unwrap();
            history.push(
              `${INCOMLEND_INTERNAL_LINK}${WITHDRAWL_DEPOSIT_LINK}/${formId}`,
            );
            if (wdCommentCategories.has(notification.category)) {
              window.dispatchEvent(new CustomEvent("openWdCommentModal"));
            }
          }
          return;
        }

        // Book meeting notification: open the related lead/account page
        if (isBookMeetingNotification(notification)) {
          const internalName = notification.context?.internal_name;
          const contactId = notification.context?.contact_id;
          const accountId =
            notification.context?.account_id ||
            notification.context?.opportunity_id;

          await markNotificationRead(notification.id).unwrap();

          if (internalName) {
            history.push(`${INCOMLEND_INTERNAL_LINK}${LEADS}/${internalName}`);
            return;
          }

          if (contactId) {
            const params = new URLSearchParams({
              [UsersTableHighlightQueryKey.CONTACT_ID]: String(contactId),
            });
            history.push(
              `${INCOMLEND_INTERNAL_LINK}${LEADS_TABLE}?${params.toString()}`,
            );
            return;
          }

          if (accountId) {
            try {
              const account = await dispatch(
                accountrtk.endpoints.getAccount.initiate(accountId),
              ).unwrap();
              if (account?.internal_name) {
                history.push(
                  `${INCOMLEND_INTERNAL_LINK}${LEADS}/${account.internal_name}`,
                );
              }
            } catch (_error: unknown) {
              // No safe destination without internal_name.
            }
          }
          return;
        }

        // Lead comment notification
        if (notification.category === "lead_comment") {
          const rawLeadId =
            notification.context?.lead_id ||
            (notification as any).entity_id ||
            notification.context?.entity_id ||
            "";
          // Strip any accidental "leads/" prefix that may come from the backend
          const leadId = String(rawLeadId).replace(/^leads\//, "");
          const leadName =
            notification.context?.lead_name ||
            notification.contact_name ||
            "Lead";
          const entityId =
            (notification as any).entity_id ||
            notification.context?.entity_id ||
            "";

          if (leadId) {
            sessionStorage.setItem("openLeadCommentModal", "true");
            sessionStorage.setItem("leadCommentModalId", leadId);
            sessionStorage.setItem("leadCommentModalName", leadName);
            sessionStorage.setItem("leadCommentEntityId", entityId);

            await markNotificationRead(notification.id).unwrap();

            if (currentUser?.is_internal) {
              history.push(`${INCOMLEND_INTERNAL_LINK}/leads/${leadId}`);
            } else {
              window.dispatchEvent(new CustomEvent("openLeadCommentModal"));
            }
          }
          return;
        }

        // Other notifications
        await markNotificationRead(notification.id).unwrap();
        if (notification.category) {
          const path = notification.category.startsWith("/")
            ? notification.category
            : `${INCOMLEND_INTERNAL_LINK}/${notification.category}`;
          history.push(path);
        }
      } catch (error) {
        // Handle error silently
      }
    },
    [markNotificationRead, currentUser, dispatch],
  );

  return { handleNotificationClick };
};
